import React, { useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  // Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getGradeColor, getGradeLabel, GRADE_CONFIG } from "../../utils/gradeColors";

const formatDate = (iso) => {
  const date = new Date(iso);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const formatMonthYear = (iso) => {
  const date = new Date(iso);
  return date.toLocaleDateString("de-CH", {
    month: "short",
    year: "numeric",
  });
};

function sortByDate(grades) {
  return [...grades].sort((a, b) => {
    const dateA = new Date(a.examDate || a.createdAt);
    const dateB = new Date(b.examDate || b.createdAt);
    return dateA - dateB;
  });
}

const GradesChart = ({ grades, subjects, selectedSemester, calculateSubjectAverage, hideChartOnMobile = false }) => {
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isDistributionOpen, setIsDistributionOpen] = useState(false);

  // Fallback calculateSubjectAverage if not provided
  const defaultCalculateSubjectAverage = (subjectId) => {
    const subjectGrades = grades.filter(grade => 
      (grade.subjectId || grade.subject_id) === subjectId
    );
    
    if (subjectGrades.length === 0) return null;
    
    const validGrades = subjectGrades
      .map(grade => ({
        value: parseFloat(grade.gradeValue),
        weight: parseFloat(grade.weight) || 1.0
      }))
      .filter(grade => !isNaN(grade.value) && grade.value > 0);
    
    if (validGrades.length === 0) return null;
    
    const totalWeightedSum = validGrades.reduce((sum, grade) => sum + (grade.value * grade.weight), 0);
    const totalWeight = validGrades.reduce((sum, grade) => sum + grade.weight, 0);
    
    const average = totalWeightedSum / totalWeight;
    return isNaN(average) ? null : average;
  };

  // Use provided function or fallback
  const subjectAverageFunction = calculateSubjectAverage || defaultCalculateSubjectAverage;

  // Calculate semester average (same logic as in SubjectsGrid)
  const calculateSemesterAverage = () => {
    if (subjects.length === 0) return null;
    
    const subjectAverages = subjects
      .map(subject => {
        const average = subjectAverageFunction(subject.id);
        const numAverage = parseFloat(average);
        return isNaN(numAverage) ? null : numAverage;
      })
      .filter(average => average !== null && average > 0);
    
    if (subjectAverages.length === 0) return null;
    
    const sum = subjectAverages.reduce((acc, avg) => acc + avg, 0);
    const finalAverage = sum / subjectAverages.length;
    
    return isNaN(finalAverage) ? null : finalAverage;
  };

  const semesterAverage = calculateSemesterAverage();

  const chartData = useMemo(() => {
    if (!grades || grades.length === 0) return [];
    return sortByDate(grades).map((grade, index) => {
      const subject = subjects?.find(
        (s) => s.id === (grade.subjectId || grade.subject_id)
      );
      const examDate = grade.examDate || grade.createdAt;

      return {
        id: index,
        date: examDate ? formatDate(examDate) : "No Date",
        monthYear: examDate ? formatMonthYear(examDate) : "No Date",
        value: parseFloat(parseFloat(grade.gradeValue).toFixed(2)),
        subject: subject?.name || "Unknown",
        description: grade.description || grade.comment || "",
        weight: parseFloat(grade.weight) || 1.0,
      };
    });
  }, [grades, subjects]);

  if (!grades || grades.length === 0) {
    return (
      <div className="card-brand-elevated p-6 mb-6">
        <div className="text-center py-8">
          <i className="fas fa-chart-line fa-3x text-gray-400 mb-4"></i>
          <h3 className="text-xl font-medium text-gray-600 mb-2">
            No Grades Yet
          </h3>
          <p className="text-gray-500">
            Add grades to see your grade progression here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card-brand-elevated p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6 gap-4">
        <div className="flex-shrink-0">
          <h2 className="text-2xl font-bold text-grey-dusk flex items-center mb-2">
            <i className="fas fa-chart-line text-sunrise-red mr-3"></i>
            Grade Progress
          </h2>
        </div>
        
        {/* Performance Overview in Header */}
        <div className="p-3 bg-pale-dawn/20 rounded-lg">
          <div className="space-y-2">
            {(() => {
              const average = semesterAverage || 0;
              const passedGrades = grades.filter(
                (g) => parseFloat(g.gradeValue) >= 4.0
              ).length;
              const passRate = (passedGrades / grades.length) * 100;

              return (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 whitespace-nowrap">
                      Avg:
                    </span>
                    <span
                      className="text-lg font-bold"
                      style={{ color: getGradeColor(average) }}
                    >
                      {Math.round(average * 100) / 100}
                    </span>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full text-white"
                      style={{ backgroundColor: getGradeColor(average) }}
                    >
                      {getGradeLabel(average)}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 whitespace-nowrap">
                      Passed:
                    </span>
                    <span className="text-xs font-medium text-green-600">
                      {passedGrades}/{grades.length} ({passRate.toFixed(0)}%)
                    </span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-grey-dusk text-white p-4 rounded-lg shadow-brand">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Total Grades</p>
              <p className="text-2xl font-bold">{grades.length}</p>
            </div>
            <i className="fas fa-graduation-cap fa-2x opacity-80"></i>
          </div>
        </div>

        <div className="bg-slate-cloud text-white p-4 rounded-lg shadow-brand">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Semester Average</p>
              <p className="text-2xl font-bold">
                {semesterAverage !== null
                  ? (Math.round(semesterAverage * 100) / 100).toString()
                  : "0.00"}
              </p>
            </div>
            <i className="fas fa-calculator fa-2x opacity-80"></i>
          </div>
        </div>

        <div className="bg-green-cloud text-white p-4 rounded-lg shadow-brand">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Best Grade</p>
              <p className="text-2xl font-bold">
                {grades.length > 0
                  ? Math.max(
                      ...grades.map((g) => parseFloat(g.gradeValue))
                    ).toFixed(1)
                  : "0.0"}
              </p>
            </div>
            <i className="fas fa-trophy fa-2x opacity-80"></i>
          </div>
        </div>

        <div className="bg-sunrise-red text-white p-4 rounded-lg shadow-brand">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Lowest Grade</p>
              <p className="text-2xl font-bold">
                {grades.length > 0
                  ? Math.min(
                      ...grades.map((g) => parseFloat(g.gradeValue))
                    ).toFixed(1)
                  : "0.0"}
              </p>
            </div>
            <i className="fas fa-arrow-down fa-2x opacity-80"></i>
          </div>
        </div>
      </div>

      {/* Chart Area */}
      <div
        className={`card shadow-sm p-3 mt-4 relative ${hideChartOnMobile ? 'hidden lg:block' : ''}`}
        style={{ minWidth: "100%", minHeight: "350px" }}
        onMouseLeave={() => setTooltipData(null)}
      >
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="monthYear"
              angle={-45}
              textAnchor="end"
              height={70}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              domain={[
                (dataMin) => Math.max(1, Math.floor(dataMin) - 0.5),
                6.3
              ]}
              ticks={[1, 2, 3, 4, 5, 6]}
              tick={{ fontSize: 12 }}
              label={{ value: "Grade", angle: -90, position: "insideLeft" }}
            />
            {/* Disable default tooltip - we'll use click-based info display */}

            {/* Grade level reference lines */}
            <ReferenceLine y={4} stroke="red" strokeDasharray="2 2" strokeOpacity={1} label="Passing Grade (4.0)" />
            
            <Line
              type="monotone"
              dataKey="value"
              stroke="#ef4444"
              strokeWidth={3}
              dot={(props) => {
                const { cx, cy, payload, index } = props;
                const grade = payload?.value || 0;
                
                // Use central grade color configuration
                const fillColor = getGradeColor(grade);
                
                return (
                  <circle
                    key={`dot-${index}`}
                    cx={cx}
                    cy={cy}
                    r={6}
                    fill={fillColor}
                    stroke={fillColor}
                    strokeWidth={2}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={(e) => {
                      if (payload) {
                        setTooltipPosition({
                          x: cx + 10,
                          y: cy - 10
                        });
                        setTooltipData(payload);
                      }
                    }}
                    onMouseLeave={() => {
                      setTooltipData(null);
                    }}
                  />
                );
              }}
            />
          </LineChart>
        </ResponsiveContainer>

        {/* Floating Tooltip */}
        {tooltipData && (
          <div 
            className="absolute bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-[70] max-w-xs pointer-events-none"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              transform: 'translate(0, -100%)'
            }}
          >
            <div className="space-y-2">
              <div className="flex items-center">
                <h6 className="font-semibold text-grey-dusk text-sm">
                  {tooltipData.subject}
                </h6>
              </div>
              
              <div className="space-y-1 text-xs">
                <div className="flex items-center">
                  <span className="font-medium text-gray-600 w-12">Grade:</span>
                  <div 
                    className="inline-block px-2 py-1 rounded text-white text-xs font-bold"
                    style={{ backgroundColor: getGradeColor(tooltipData.value) }}
                  >
                    {tooltipData.value}
                  </div>
                </div>
                
                <div className="flex items-center">
                  <span className="font-medium text-gray-600 w-12">Date:</span>
                  <span className="text-grey-dusk">{tooltipData.date}</span>
                </div>
                
                <div className="flex items-center">
                  <span className="font-medium text-gray-600 w-12">Weight:</span>
                  <span className="text-grey-dusk">{tooltipData.weight}</span>
                </div>
                
                {tooltipData.description && (
                  <div className="pt-2 border-t border-gray-200">
                    <span className="font-medium text-gray-600 block mb-1">Description:</span>
                    <p className="text-xs text-grey-dusk">{tooltipData.description}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Performance Analysis */}
      <div className="mt-6">
        {/* Grade Distribution - Collapsible */}
        <div className="p-4 bg-pale-dawn/20 rounded-lg">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setIsDistributionOpen(!isDistributionOpen)}
          >
            <h4 className="font-medium text-grey-dusk">
              <i className="fas fa-chart-pie mr-2 text-sunrise-red"></i>
              Grade Distribution
            </h4>
            <i className={`fas fa-chevron-${isDistributionOpen ? 'up' : 'down'} text-grey-dusk`}></i>
          </div>
          
          {isDistributionOpen && (
            <div className="space-y-2 mt-3">
            {(() => {
              // Use GRADE_CONFIG to generate distribution
              const distribution = GRADE_CONFIG.map(config => ({
                range: `${config.label} (${config.min.toFixed(2)}-${config.max.toFixed(2)})`,
                count: grades.filter(g => {
                  const val = parseFloat(g.gradeValue);
                  // Handle the 6.0 edge case for the top range
                  if (config.min === 5.75 && val > 6.0) return true;
                  return val >= config.min && val <= config.max;
                }).length,
                color: config.color
              }));

              return distribution.map(
                (item, index) => (
                  <div
                    key={item.range}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      <div
                        className="w-3 h-3 rounded mr-2"
                        style={{ backgroundColor: item.color }}
                      ></div>
                      <span className="text-sm text-grey-dusk">
                        {item.range}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-grey-dusk">
                      {item.count} (
                      {grades.length > 0 ? ((item.count / grades.length) * 100).toFixed(1) : 0}%)
                    </span>
                  </div>
                )
              );
            })()}
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default GradesChart;
